/**
 * Board state lives in the URL, so a link, a refresh and the back button land
 * where the person was: the tab, the search, the filters, the sort, the page
 * and the open row. Writes replace the history entry (a sort is not a
 * navigation). Changing anything but the page or the open row returns to
 * page 1 and closes the open row, because the row may no longer be there.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface BoardUrlState {
  tab: string;
  q: string;
  page: number;
  sort: string | null;
  dir: 'asc' | 'desc';
  open: string | null;
  filters: Record<string, string>;
}

export function useBoardUrlState(filterKeys: string[], defaultTab = 'all') {
  const [params, setParams] = useSearchParams();
  const filterSig = filterKeys.join(',');
  const state: BoardUrlState = useMemo(() => {
    const page = parseInt(params.get('page') || '1', 10);
    return {
      tab: params.get('tab') || defaultTab,
      q: params.get('q') || '',
      page: Number.isFinite(page) && page > 0 ? page : 1,
      sort: params.get('sort'),
      dir: params.get('dir') === 'desc' ? 'desc' : 'asc',
      open: params.get('open'),
      filters: Object.fromEntries(filterSig.split(',').filter(Boolean).map((k) => [k, params.get(k) || ''])),
    };
  }, [params, defaultTab, filterSig]);

  const update = useCallback((patch: Partial<Omit<BoardUrlState, 'filters'>> & { filters?: Record<string, string> }) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      const put = (k: string, v: string | number | null | undefined, dflt?: string) => {
        if (v === null || v === undefined || v === '' || String(v) === dflt) next.delete(k); else next.set(k, String(v));
      };
      const resets = Object.keys(patch).some((k) => k !== 'page' && k !== 'open');
      if ('tab' in patch) put('tab', patch.tab, defaultTab);
      if ('q' in patch) put('q', patch.q);
      if ('sort' in patch) put('sort', patch.sort);
      // A direction means nothing without a sort: clearing the sort clears it.
      if ('sort' in patch && !patch.sort) next.delete('dir');
      else if ('dir' in patch) put('dir', patch.dir, 'asc');
      if (patch.filters) for (const [k, v] of Object.entries(patch.filters)) put(k, v);
      if (resets) { next.delete('page'); next.delete('open'); }
      if ('page' in patch) { put('page', patch.page, '1'); next.delete('open'); }
      if ('open' in patch) put('open', patch.open);
      return next;
    }, { replace: true });
  }, [setParams, defaultTab]);

  return [state, update] as const;
}
